import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Star, Send } from 'lucide-react';

interface SimpleRatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  onSubmitRating: (rating: { orderId: string; rating: number; comment: string }) => void;
}

export function SimpleRatingModal({ 
  isOpen, 
  onClose, 
  orderId, 
  onSubmitRating 
}: SimpleRatingModalProps) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleStarClick = (starRating: number) => {
    setRating(starRating);
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      alert('Please select a rating');
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      onSubmitRating({
        orderId,
        rating,
        comment: comment.trim()
      });
      setIsSubmitting(false);
      onClose();
      
      // Reset form
      setRating(0);
      setComment('');
    }, 1000);
  };

  const getRatingText = (rating: number) => {
    if (rating === 0) return 'Select Rating';
    if (rating === 1) return 'Poor';
    if (rating === 2) return 'Fair';
    if (rating === 3) return 'Good';
    if (rating === 4) return 'Very Good';
    if (rating === 5) return 'Excellent';
    return '';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-md mx-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900 font-display">
            Rate Your Order
          </DialogTitle>
          <p className="text-gray-600 text-sm">
            Order #{orderId}
          </p>
        </DialogHeader>

        <div className="space-y-6">
          {/* Star Rating */}
          <div className="text-center space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">How was your experience?</h3>
            
            <div className="flex justify-center space-x-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => handleStarClick(star)}
                  className={`w-10 h-10 transition-all duration-200 hover:scale-110 ${
                    star <= rating 
                      ? 'text-yellow-400 fill-current' 
                      : 'text-gray-300 hover:text-yellow-300'
                  }`}
                >
                  <Star className="w-full h-full" />
                </button>
              ))}
            </div>
            
            {rating > 0 && (
              <div className="text-center">
                <span className="inline-block bg-emerald-100 text-emerald-800 text-sm font-medium px-3 py-1 rounded-full">
                  {getRatingText(rating)}
                </span>
              </div>
            )}
          </div>

          {/* Comment Section */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">
              Share your feedback (optional)
            </label>
            <Textarea
              placeholder="Tell us about your experience with the food, delivery, and service..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
              rows={4}
            />
          </div>

          {/* Submit Button */}
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 h-12"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={rating === 0 || isSubmitting}
              className="flex-1 h-12 btn-primary"
            >
              {isSubmitting ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Submitting...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Send className="w-4 h-4" />
                  <span>Submit Rating</span>
                </div>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}